import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/api/auth';
import { getApiErrorMessage } from '@/utils/apiError';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await authApi.adminForgotPassword({ email });
      setSuccess(response.message || t('auth.reset.emailSent'));
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t('auth.reset.requestFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold">{t('auth.reset.forgotTitle')}</h2>
        <p className="mt-2 text-muted-foreground">{t('auth.reset.forgotSubtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center">
            {error}
          </div>
        )}

        {success && (
          <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-700 text-sm text-center">
            {success}
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium">
            {t('auth.login.emailLabel')}
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={t('auth.login.emailPlaceholder')}
            dir="ltr"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {isLoading ? t('auth.reset.sending') : t('auth.reset.sendLink')}
        </button>

        <div className="text-center">
          <Link to="/admin-login" className="text-sm text-muted-foreground hover:text-primary">
            {t('auth.reset.backToAdminLogin')}
          </Link>
        </div>
      </form>
    </div>
  );
}
